'use client';

import { useEffect, useState } from 'react';
import {
  Eye, Save, CheckCircle2, AlertTriangle, Check, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  crearSolicitud, actualizarSolicitud, listarFirmantes,
  CANALES, DECISIONES,
  type Solicitud, type Firmante, type Canal, type DecisionComite,
} from '@/lib/preRadicacion';
import ActaRepartoDialog from '@/components/ActaRepartoDialog';
import { usuarioActual } from '@/lib/instruccion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import CampoPDF from '@/components/CampoPDF';

const soloFecha = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');
const NINGUNO = '__NINGUNO__';

// Pasos del asistente de pre-radicación (avance del proceso en el Libro radicador).
const PASOS = ['Pre-radicación', 'Decisión comité', 'Asignación y acta de reparto'];

interface FormSol {
  fechaIngreso: string; canal: Canal | '';
  radicadoOrigen: string; porVentanillaUnica: boolean; fechaRespuestaVentanilla: string;
  documentoKey: string; anexoKey: string;
  decisionComite: DecisionComite | ''; justificacionComite: string;
  comiteKey: string; fechaDecisionComite: string;
  abogadoId: string; tecnologoApoyoId: string; tecnicoId: string; directorId: string;
  fechaAsignacion: string; plazoAbogado: string;
}

function aForm(s: Solicitud | null): FormSol {
  return {
    fechaIngreso: soloFecha(s?.fecha_ingreso), canal: (s?.canal ?? '') as Canal | '',
    radicadoOrigen: s?.radicado_origen ?? '', porVentanillaUnica: s?.por_ventanilla_unica ?? false,
    fechaRespuestaVentanilla: soloFecha(s?.fecha_respuesta_ventanilla),
    documentoKey: s?.documento_key ?? '', anexoKey: s?.anexo_key ?? '',
    decisionComite: (s?.decision_comite ?? '') as DecisionComite | '',
    justificacionComite: s?.justificacion_comite ?? '', comiteKey: s?.comite_key ?? '',
    fechaDecisionComite: soloFecha(s?.fecha_decision_comite),
    abogadoId: s?.abogado_id ?? '', tecnologoApoyoId: s?.tecnologo_apoyo_id ?? '',
    tecnicoId: s?.tecnico_id ?? '', directorId: s?.director_id ?? '',
    fechaAsignacion: soloFecha(s?.fecha_asignacion),
    plazoAbogado: soloFecha(s?.plazo_abogado_fecha),
  };
}

/**
 * Etapa 1 del Libro radicador: pre-radicación (ingreso, comité, asignación + acta).
 * Guarda una SolicitudRadicacion y reporta su estado (completa/faltantes) al padre,
 * que decide si habilita la Etapa 2 (datos de la queja).
 */
export default function PreRadicacionForm({
  solicitud,
  onGuardada,
}: {
  solicitud: Solicitud | null;
  onGuardada: (s: Solicitud) => void;
}) {
  const [form, setForm] = useState<FormSol>(() => aForm(solicitud));
  const [firmantes, setFirmantes] = useState<Firmante[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [actaAbierta, setActaAbierta] = useState(false); // diálogo de previsualización del acta
  const [paso, setPaso] = useState(0); // 0=ingreso · 1=comité · 2=asignación

  // Sincroniza el form cuando cambia la solicitud cargada (p. ej. al retomar una).
  useEffect(() => { setForm(aForm(solicitud)); }, [solicitud?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { listarFirmantes().then(setFirmantes).catch(() => setFirmantes([])); }, []);

  // Preselecciona al Técnico administrativo (Elaboró) con el usuario en sesión si aplica.
  useEffect(() => {
    const yo = usuarioActual()?.id;
    if (!yo || form.tecnicoId) return;
    if (firmantes.some((f) => f.id === yo && f.rol === 'TECNICO_ADMINISTRATIVO')) {
      setForm((f) => (f.tecnicoId ? f : { ...f, tecnicoId: yo }));
    }
  }, [firmantes, form.tecnicoId]);

  function set<K extends keyof FormSol>(k: K, v: FormSol[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // La ventanilla única (checkbox + fecha de respuesta + PDFs) aplica a Correo, Físico y Otro.
  const esVentanilla = form.canal === 'CORREO' || form.canal === 'FISICO' || form.canal === 'OTRO';
  const abogados = firmantes.filter((f) => f.rol === 'CONTRATISTA_INSTRUCCION');
  const tecnologos = firmantes.filter((f) => f.rol === 'TECNOLOGO_APOYO');
  const directores = firmantes.filter((f) => f.rol === 'DIRECTOR_INSTRUCCION');
  const tecnicos = firmantes.filter((f) => f.rol === 'TECNICO_ADMINISTRATIVO');

  const labelFechaDecision =
    form.decisionComite === 'PROCEDE' ? 'Fecha de aprobación del comité'
    : form.decisionComite === 'NO_PROCEDE' ? 'Fecha de denegación'
    : form.decisionComite === 'SE_OMITE' ? 'Fecha de omisión'
    : 'Fecha de la decisión';

  // ¿Cada paso quedó diligenciado? (para el chulo del stepper).
  const pasoDone = [
    Boolean(form.fechaIngreso && form.canal),
    form.decisionComite !== '',
    form.abogadoId !== '',
  ];

  async function guardar() {
    if (!form.fechaIngreso || !form.canal) {
      toast.error('Indique la fecha de ingreso y el canal.');
      return;
    }
    setGuardando(true);
    try {
      const cuerpo = {
        fechaIngreso: form.fechaIngreso,
        canal: form.canal as Canal,
        radicadoOrigen: form.radicadoOrigen.trim() || undefined,
        porVentanillaUnica: esVentanilla ? form.porVentanillaUnica : false,
        fechaRespuestaVentanilla: esVentanilla ? (form.fechaRespuestaVentanilla || undefined) : undefined,
        documentoKey: form.documentoKey || undefined,
        anexoKey: form.anexoKey || undefined,
        decisionComite: form.decisionComite || undefined,
        justificacionComite: form.justificacionComite.trim() || undefined,
        comiteKey: form.comiteKey || undefined,
        fechaDecisionComite: form.fechaDecisionComite || undefined,
        abogadoId: form.abogadoId || undefined,
        tecnologoApoyoId: form.tecnologoApoyoId || undefined,
        tecnicoId: form.tecnicoId || undefined,
        directorId: form.directorId || undefined,
        fechaAsignacion: form.fechaAsignacion || undefined,
        plazoAbogadoFecha: form.plazoAbogado || undefined,
      };
      // `crear` solo acepta datos de ingreso; el resto (comité/asignación) va por `actualizar`.
      // Solicitud nueva: se crea con lo mínimo y luego se actualiza con todo el cuerpo.
      const base = solicitud
        ?? await crearSolicitud({ fechaIngreso: form.fechaIngreso, canal: form.canal as Canal });
      const saved = await actualizarSolicitud(base.id, cuerpo);
      onGuardada(saved);
      if (saved.completa) {
        toast.success('Pre-radicación completa. Continúe con los datos de la notificación disciplinaria.');
      } else {
        toast.info('Guardado en «Expedientes no iniciados».', {
          description: `Falta: ${saved.faltantes.join(' · ')}`,
        });
      }
    } catch (err) {
      toast.error('No se pudo guardar', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  // Previsualiza el acta dentro de la app (diálogo con el .docx oficial vía docx-preview).
  // Requiere solicitud guardada + contratista asignado.
  function previsualizarActa() {
    if (!solicitud) { toast.error('Guarde primero la solicitud.'); return; }
    if (!solicitud.abogado_id) {
      toast.error('Asigne el contratista y guarde antes de generar el acta.');
      return;
    }
    setActaAbierta(true);
  }

  return (
    <div className="space-y-5">
      {/* Stepper de 3 pasos: avance de la pre-radicación (clicable) */}
      <div className="flex flex-wrap gap-2">
        {PASOS.map((label, i) => {
          const activo = paso === i;
          const done = pasoDone[i];
          return (
            <button
              key={label}
              type="button"
              onClick={() => setPaso(i)}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition',
                activo ? 'border-primary bg-primary/5 font-medium' : 'hover:border-primary/50',
              )}
            >
              <span className={cn(
                'flex size-6 items-center justify-center rounded-full text-xs',
                done ? 'bg-primary text-primary-foreground'
                  : activo ? 'border border-primary text-primary'
                  : 'bg-muted text-muted-foreground',
              )}>
                {done ? <Check className="size-3.5" /> : i + 1}
              </span>
              {label}
            </button>
          );
        })}
      </div>

      {/* Paso 1 · Ingreso */}
      {paso === 0 && (
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="fi">Fecha de ingreso</Label>
            <Input id="fi" type="date" value={form.fechaIngreso} onChange={(e) => set('fechaIngreso', e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>¿Por dónde ingresó?</Label>
            <Select value={form.canal} onValueChange={(v) => set('canal', v as Canal)}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
              <SelectContent>
                {CANALES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.canal && (
          <div className="space-y-2">
            <Label htmlFor="rad">
              {form.canal === 'SAIA' ? 'Radicado SAIA' : 'Radicado'}{' '}
              <span className="text-xs text-muted-foreground">(se le agrega -XXX consecutivo)</span>
            </Label>
            <Input id="rad" value={form.radicadoOrigen} onChange={(e) => set('radicadoOrigen', e.target.value)} placeholder="Ej.: 2026-000123" />
            {solicitud?.radicado_completo && (
              <p className="text-xs text-muted-foreground">Radicado asignado: <strong>{solicitud.radicado_completo}</strong></p>
            )}
          </div>
        )}

        {esVentanilla && (
          <div className="space-y-3 rounded-md border p-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.porVentanillaUnica} onChange={(e) => set('porVentanillaUnica', e.target.checked)} />
              Ingresó por ventanilla única
            </label>
            <div className="space-y-2 sm:max-w-xs">
              <Label htmlFor="frv">Fecha de respuesta de ventanilla única</Label>
              <Input id="frv" type="date" value={form.fechaRespuestaVentanilla} onChange={(e) => set('fechaRespuestaVentanilla', e.target.value)} />
            </div>
            <CampoPDF label="Documento (PDF)" carpeta="QUEJA" value={form.documentoKey} onChange={(path) => set('documentoKey', path)} />
            <CampoPDF label="Anexo (PDF)" carpeta="ANEXOS" value={form.anexoKey} onChange={(path) => set('anexoKey', path)} />
          </div>
        )}
      </section>
      )}

      {/* Paso 2 · Decisión de comité */}
      {paso === 1 && (
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Decisión</Label>
            <Select value={form.decisionComite} onValueChange={(v) => set('decisionComite', v as DecisionComite)}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
              <SelectContent>
                {DECISIONES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.decisionComite && (
            <div className="space-y-2">
              <Label htmlFor="fdc">{labelFechaDecision}</Label>
              <Input id="fdc" type="date" value={form.fechaDecisionComite} onChange={(e) => set('fechaDecisionComite', e.target.value)} />
            </div>
          )}
        </div>
        {form.decisionComite && (
          <>
            <div className="space-y-2">
              <Label htmlFor="just">Justificación</Label>
              <Textarea id="just" value={form.justificacionComite} onChange={(e) => set('justificacionComite', e.target.value)} rows={3} />
            </div>
            <CampoPDF label="Soporte de la decisión (PDF)" carpeta="QUEJA" value={form.comiteKey} onChange={(path) => set('comiteKey', path)} />
            {form.decisionComite === 'NO_PROCEDE' && (
              <p className="text-xs text-amber-600">NO PROCEDE: la solicitud no pasará al Libro radicador.</p>
            )}
          </>
        )}
      </section>
      )}

      {/* Paso 3 · Asignación y acta de reparto */}
      {paso === 2 && (
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectFirmante label="Contratista asignado (Quien recibe)" value={form.abogadoId} opciones={abogados.length ? abogados : firmantes} onChange={(v) => set('abogadoId', v)} />
          <SelectFirmante label="Tecnólogo de apoyo" value={form.tecnologoApoyoId} opciones={tecnologos} onChange={(v) => set('tecnologoApoyoId', v)} />
          <SelectFirmante label="Director de Instrucción (firma)" value={form.directorId} opciones={directores.length ? directores : firmantes} onChange={(v) => set('directorId', v)} />
          <SelectFirmante label="Técnico administrativo (Elaboró)" value={form.tecnicoId} opciones={tecnicos.length ? tecnicos : firmantes} onChange={(v) => set('tecnicoId', v)} />
        </div>
        <p className="text-xs text-muted-foreground">
          El acta lleva <strong>3 firmas</strong>: <strong>Director de Instrucción</strong>,
          <strong> Contratista/Funcionario asignado</strong> (Quien recibe) y
          <strong> Técnico administrativo</strong> (Elaboró). Elija quién firma cada una.
        </p>

        {/* Fecha de reparto: fecha de asignación + plazo para el abogado */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="fasig">Fecha de asignación del proceso</Label>
            <Input id="fasig" type="date" value={form.fechaAsignacion} onChange={(e) => set('fechaAsignacion', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plazo">Plazo para que el abogado resuelva</Label>
            <Input id="plazo" type="date" value={form.plazoAbogado} onChange={(e) => set('plazoAbogado', e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={previsualizarActa} disabled={!solicitud}>
            <Eye className="size-4" /> Previsualizar acta de reparto
          </Button>
          <span className="text-xs text-muted-foreground">
            {solicitud ? 'Se abre el formato oficial (con logos); desde ahí se descarga en .docx o PDF.'
              : 'Guarde la solicitud para generar el acta.'}
          </span>
        </div>

        {actaAbierta && solicitud && (
          <ActaRepartoDialog
            // Refleja las selecciones actuales aunque aún no se hayan guardado.
            solicitud={{
              ...solicitud,
              abogado_id: form.abogadoId || solicitud.abogado_id,
              director_id: form.directorId || null,
              tecnico_id: form.tecnicoId || null,
            }}
            firmantes={firmantes}
            onClose={() => setActaAbierta(false)}
          />
        )}
      </section>
      )}

      {/* Navegación entre pasos + guardar + estado de completitud */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <div className="flex gap-2">
          <Button type="button" variant="outline" disabled={paso === 0} onClick={() => setPaso((p) => Math.max(0, p - 1))}>
            <ChevronLeft className="size-4" /> Anterior
          </Button>
          {paso < PASOS.length - 1 && (
            <Button type="button" variant="outline" onClick={() => setPaso((p) => Math.min(PASOS.length - 1, p + 1))}>
              Siguiente <ChevronRight className="size-4" />
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm">
            {solicitud?.completa ? (
              <span className="flex items-center gap-1.5 text-primary"><CheckCircle2 className="size-4" /> Pre-radicación completa</span>
            ) : solicitud ? (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <AlertTriangle className="size-4" /> Falta: {solicitud.faltantes.join(' · ')}
              </span>
            ) : (
              <span className="text-muted-foreground">Sin guardar aún.</span>
            )}
          </div>
          <Button type="button" onClick={guardar} disabled={guardando}>
            <Save className="size-4" /> {guardando ? 'Guardando…' : 'Guardar pre-radicación'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SelectFirmante({
  label, value, opciones, onChange,
}: {
  label: string; value: string; opciones: Firmante[]; onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value || NINGUNO} onValueChange={(v) => onChange(v === NINGUNO ? '' : v)}>
        <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NINGUNO}>— Sin asignar —</SelectItem>
          {opciones.map((f) => (
            <SelectItem key={f.id} value={f.id}>
              {f.nombre}{f.tieneFirma ? '' : ' (sin firma)'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
